import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  Groups,
  Refresh,
  Search,
  WorkspacePremium,
} from "@mui/icons-material";
import {
  getAdminSubscriptions,
  isSubscriptionAccessActive,
} from "../services/subscriptionService";
import usePageTitle from "../hooks/usePageTitle";
import { getAdminUsers } from "../services/adminUsersService";

const STATUS_LABELS = {
  active: "Activa",
  trialing: "Prueba",
  past_due: "Pago pendiente",
  canceled: "Cancelada",
  unpaid: "Impagada",
  incomplete: "Incompleta",
  incomplete_expired: "Incompleta expirada",
};

const STATUS_COLORS = {
  active: "success",
  trialing: "info",
  past_due: "warning",
  canceled: "default",
  unpaid: "error",
  incomplete: "warning",
  incomplete_expired: "error",
};

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString("es-ES") : "Pendiente";
}

function formatPrice(subscription) {
  if (subscription.unit_amount == null) return "No disponible";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: (subscription.currency || "eur").toUpperCase(),
  }).format(subscription.unit_amount / 100);
}

export default function AdminLicenciasPageModern() {
  usePageTitle("Gestión de licencias");
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [subscriptions, profiles] = await Promise.all([
        getAdminSubscriptions(),
        getAdminUsers(),
      ]);
      const profilesByEmail = new Map(
        profiles.filter((profile) => profile.email?.trim()).map((profile) => [
          profile.email.trim().toLowerCase(),
          profile,
        ])
      );
      setSubscriptions(subscriptions.map((subscription) => ({
        ...subscription,
        subscriber_name: profilesByEmail.get(subscription.subscriber_email?.trim().toLowerCase())
          ?.full_name?.trim() || "",
      })));
    } catch (loadError) {
      setError(loadError.message || "No se pudieron cargar las suscripciones.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredSubscriptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return subscriptions;
    return subscriptions.filter((subscription) =>
      [
        subscription.subscriber_email,
        subscription.subscriber_name,
        subscription.plan_name,
        subscription.plan_key,
        subscription.status,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [search, subscriptions]);

  const stats = useMemo(() => {
    const active = subscriptions.filter((subscription) =>
      isSubscriptionAccessActive(subscription)
    );
    return {
      total: subscriptions.length,
      active: active.length,
      clubs: active.filter(
        (subscription) => subscription.scope === "club"
      ).length,
      clubCapacity: active
        .filter((subscription) => subscription.scope === "club")
        .reduce((total, subscription) => total + subscription.seats_total, 0),
    };
  }, [subscriptions]);

  const statCards = [
    { label: "Suscripciones", value: stats.total, icon: <WorkspacePremium />, color: "primary" },
    { label: "Con acceso activo", value: stats.active, icon: <CheckCircle />, color: "success" },
    { label: "Planes de club", value: stats.clubs, icon: <Groups />, color: "info" },
    { label: "Cupo contratado", value: stats.clubCapacity, icon: <Groups />, color: "secondary" },
  ];

  return (
    <Box className="wp-page" component="section" sx={{ flex: 1, width: "100%", maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, py: 4, bgcolor: "background.default", color: "text.primary" }}>
      <Stack spacing={3}>
        <Stack
          className="wp-heading"
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
        >
          <Box>
            <Typography component="h1" variant="h4" fontWeight={800}>
              Gestión de licencias
            </Typography>
            <Typography color="text.secondary">
              Suscripciones sincronizadas desde Stripe y usadas para autorizar el acceso.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            disabled={loading}
            onClick={load}
          >
            Actualizar
          </Button>
        </Stack>

        <Alert severity="info">
          Esta vista es de consulta. Las altas, cancelaciones y cambios de plan se
          gestionan en Stripe para mantener la facturación y los permisos sincronizados.
        </Alert>
        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

        <Grid container spacing={2}>
          {statCards.map((card) => (
            <Grid key={card.label} item xs={6} md={3}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ color: `${card.color}.main`, display: "grid", placeItems: "center" }}>
                      {card.icon}
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={800}>{card.value}</Typography>
                      <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card variant="outlined">
          <CardContent sx={{ p: 0 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}
            >
              <Box>
                <Typography variant="h6" fontWeight={800}>Todas las suscripciones</Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredSubscriptions.length} de {subscriptions.length} mostradas
                </Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Buscar por nombre, email o plan"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { sm: 320 } }}
              />
            </Stack>
            {loading ? (
              <Box sx={{ display: "grid", placeItems: "center", minHeight: 260 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small" aria-label="Todas las suscripciones">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'background.default' }}>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Precio</TableCell>
                      <TableCell>Renovación</TableCell>
                      <TableCell>Cupo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSubscriptions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <Typography color="text.secondary">
                            No hay suscripciones que coincidan con la búsqueda.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSubscriptions.map((subscription) => (
                        <TableRow key={subscription.id} hover>
                          <TableCell>
                            <Typography fontWeight={700}>
                              {subscription.subscriber_name || "Sin nombre"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {subscription.scope === "club" ? "Club" : "Individual"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ overflowWrap: "anywhere", minWidth: 180 }}>
                            {subscription.subscriber_email || "Sin email"}
                          </TableCell>
                          <TableCell>{subscription.plan_name}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={STATUS_LABELS[subscription.status] || subscription.status}
                              color={STATUS_COLORS[subscription.status] || "default"}
                            />
                          </TableCell>
                          <TableCell>
                            {formatPrice(subscription)}
                            {subscription.billing_interval && (
                              <Typography variant="body2" color="text.secondary">
                                /{subscription.billing_interval === "year" ? "año" : "mes"}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {subscription.cancel_at_period_end
                                ? "Finaliza el"
                                : "Renueva el"} {formatDate(subscription.current_period_end)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {subscription.scope === "club"
                              ? subscription.seats_total
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
